import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  GraduationCap, 
  AlertTriangle, 
  TrendingDown, 
  CreditCard, 
  Users, 
  Calendar,
  MessageSquare,
  ExternalLink,
  Clock,
  Target,
  BookOpen,
  UserX,
  Flag,
  Eye,
  Heart,
  Megaphone,
  ArrowRight
} from 'lucide-react';
import TaskWidget from '@/components/TaskWidget';
import { useAuth } from '@/hooks/useAuth';
import { useDataCache } from '@/hooks/useDataCache';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PromotionGroup {
  id: number;
  code: string;
  name: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
  maxStudents: number | null;
  programName: string | null;
  studentCount: number;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { getCompanyData, cachedData } = useDataCache();
  const navigate = useNavigate();
  const [promotionGroups, setPromotionGroups] = useState<PromotionGroup[]>([]);

  useEffect(() => {
    getCompanyData();
  }, [getCompanyData]);

  // Check if user is a student - students shouldn't see mission/vision/values
  const isStudent = user?.role?.toLowerCase() === 'alumno' || user?.role?.toLowerCase() === 'estudiante';
  const isTeacher = user?.role?.toLowerCase() === 'docente' || user?.role?.toLowerCase() === 'profesor' || user?.role?.toLowerCase() === 'teacher';
  const canSeePromotionAlert = !isStudent && !isTeacher;

  useEffect(() => {
    if (!canSeePromotionAlert) return;
    fetch('/api/academic-periods/in-promotion', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setPromotionGroups(Array.isArray(data) ? data : []))
      .catch(() => setPromotionGroups([]));
  }, [canSeePromotionAlert]);

  const handleKpiAction = (kpiType: string) => {
    // Aquí se implementaría la acción directa desde el KPI
    console.log(`Acción directa para KPI: ${kpiType}`);
  };

  // Get company mission, vision and values
  const companyData = cachedData.company as any;
  const mision = companyData?.mision;
  const vision = companyData?.vision;
  const valores = companyData?.valores;

  // Only show mission/vision/values if at least one is filled and user is not a student
  const showCompanyValues = !isStudent && (mision || vision || valores);

  return (
    <div className="space-y-8">
      {/* Misión, Visión y Valores - Solo para roles no-estudiantes */}
      {showCompanyValues && (
        <div className="grid gap-6 md:grid-cols-3">
          {/* Misión */}
          {mision && (
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-cyan-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-lg font-bold text-blue-900 flex items-center">
                  <Flag className="h-5 w-5 mr-2 text-blue-600" />
                  Misión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{mision}</p>
              </CardContent>
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200/30 rounded-full blur-2xl"></div>
            </Card>
          )}

          {/* Visión */}
          {vision && (
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-lg font-bold text-purple-900 flex items-center">
                  <Eye className="h-5 w-5 mr-2 text-purple-600" />
                  Visión
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{vision}</p>
              </CardContent>
              <div className="absolute top-0 right-0 w-24 h-24 bg-purple-200/30 rounded-full blur-2xl"></div>
            </Card>
          )}

          {/* Valores */}
          {valores && (
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-lg font-bold text-emerald-900 flex items-center">
                  <Heart className="h-5 w-5 mr-2 text-emerald-600" />
                  Valores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{valores}</p>
              </CardContent>
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-200/30 rounded-full blur-2xl"></div>
            </Card>
          )}
        </div>
      )}

      {/* Grupos en Promoción - alert banner for admin/staff roles */}
      {canSeePromotionAlert && promotionGroups.length > 0 && (
        <div className="relative overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6 shadow-lg">
          <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-8 w-20 h-20 bg-orange-200/20 rounded-full blur-2xl pointer-events-none"></div>

          <div className="flex items-start gap-4 mb-5">
            <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-amber-100 border border-amber-200 flex items-center justify-center">
              <Megaphone className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                Grupos en Promoción
                <Badge className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5">
                  {promotionGroups.length}
                </Badge>
              </h3>
              <p className="text-sm text-amber-700 mt-0.5">
                Los siguientes grupos están disponibles para inscripción de nuevos alumnos.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {promotionGroups.map(g => {
              const spotsLeft = g.maxStudents != null ? g.maxStudents - g.studentCount : null;
              const spotsColor = spotsLeft == null
                ? 'text-gray-500'
                : spotsLeft === 0
                  ? 'text-red-600 font-semibold'
                  : spotsLeft <= 5
                    ? 'text-orange-600 font-semibold'
                    : 'text-green-700 font-semibold';
              return (
                <div
                  key={g.id}
                  className="bg-white/80 border border-amber-200 rounded-xl p-4 flex flex-col gap-1 hover:bg-white hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate('/app/catalogos/periodos')}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-900 text-sm truncate">{g.name}</span>
                    <ArrowRight className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  </div>
                  {g.programName && (
                    <span className="text-xs text-gray-500 truncate">{g.programName}</span>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-xs text-gray-600">
                      <Users className="h-3 w-3" />
                      {g.studentCount} inscritos
                    </span>
                    {spotsLeft != null && (
                      <span className={`text-xs ${spotsColor}`}>
                        {spotsLeft === 0 ? 'Sin cupo' : `${spotsLeft} cupos libres`}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
              onClick={() => navigate('/app/catalogos/periodos')}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Gestionar en Periodos Académicos
            </Button>
          </div>
        </div>
      )}

      {/* Modern Header with gradient background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-3xl p-8 border border-blue-100">
        <div className="relative z-10">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            📊 Visión 360° en tiempo real
          </div>
          <h2 className="text-4xl font-bold tracking-tight text-gray-900 mb-2">
            Centro de Control Escolar
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl">
            Monitoreo integral del estado académico, financiero y operativo de tu institución educativa con detección temprana de riesgos.
          </p>
        </div>
        
        {/* Background decorations */}
        <div className="absolute top-4 right-8 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-4 right-16 w-16 h-16 bg-gradient-to-br from-emerald-400/20 to-blue-400/20 rounded-full blur-xl"></div>
      </div>

      {/* KPI Cards - School Management */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Matrícula Activa */}
        <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-emerald-50 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleKpiAction('matricula')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Matrícula Activa</CardTitle>
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-700 mb-1">1,247</div>
            <p className="text-sm text-green-600 flex items-center">
              <span className="mr-1">↗</span>+3.2% vs año anterior
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-xs opacity-70 hover:opacity-100">
              <ExternalLink className="h-3 w-3 mr-1" />
              Ver estudiantes
            </Button>
          </CardContent>
          <div className="absolute top-0 right-0 w-20 h-20 bg-green-200/30 rounded-full blur-xl"></div>
        </Card>

        {/* Morosidad */}
        <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-red-50 to-pink-50 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleKpiAction('morosidad')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Morosidad</CardTitle>
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
              <CreditCard className="h-6 w-6 text-red-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-700 mb-1">8.3%</div>
            <p className="text-sm text-red-600 flex items-center">
              <span className="mr-1">⚠</span>47 estudiantes en riesgo
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-xs opacity-70 hover:opacity-100">
              <ExternalLink className="h-3 w-3 mr-1" />
              Gestionar pagos
            </Button>
          </CardContent>
          <div className="absolute top-0 right-0 w-20 h-20 bg-red-200/30 rounded-full blur-xl"></div>
        </Card>

        {/* Rezago Académico */}
        <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-orange-50 to-yellow-50 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleKpiAction('rezago')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Rezago Académico</CardTitle>
            <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
              <TrendingDown className="h-6 w-6 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-700 mb-1">12</div>
            <p className="text-sm text-orange-600">estudiantes con 3+ materias reprobadas</p>
            <Button size="sm" variant="ghost" className="mt-2 text-xs opacity-70 hover:opacity-100">
              <ExternalLink className="h-3 w-3 mr-1" />
              Plan de recuperación
            </Button>
          </CardContent>
          <div className="absolute top-0 right-0 w-20 h-20 bg-orange-200/30 rounded-full blur-xl"></div>
        </Card>

        {/* Alertas Críticas */}
        <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-purple-50 to-indigo-50 cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => handleKpiAction('alertas')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-700">Alertas Críticas</CardTitle>
            <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-700 mb-1">7</div>
            <p className="text-sm text-purple-600">casos que requieren atención inmediata</p>
            <Button size="sm" variant="ghost" className="mt-2 text-xs opacity-70 hover:opacity-100">
              <ExternalLink className="h-3 w-3 mr-1" />
              Ver casos
            </Button>
          </CardContent>
          <div className="absolute top-0 right-0 w-20 h-20 bg-purple-200/30 rounded-full blur-xl"></div>
        </Card>
      </div>

      {/* Mis Tareas */}
      <TaskWidget showCompleted={false} maxItems={5} />

      {/* Priorización y Acción Directa */}
      <div className="grid gap-6 md:grid-cols-3">
        {/* Atender HOY */}
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-red-50 to-pink-50 rounded-t-lg">
            <CardTitle className="text-gray-800 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-red-600" />
              Atender HOY
            </CardTitle>
            <CardDescription className="text-gray-600">Casos que requieren acción inmediata</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-xl border border-red-100">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="h-5 w-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">María González - Grupo 3A</p>
                  <p className="text-sm text-gray-600">15 días sin asistir • Contactar familia</p>
                  <Button size="sm" className="mt-2 bg-red-600 hover:bg-red-700 text-white">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Enviar alerta
                  </Button>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-xl border border-yellow-100">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Pago vencido - Carlos Ruiz</p>
                  <p className="text-sm text-gray-600">$3,500 • 30 días de vencimiento</p>
                  <Button size="sm" className="mt-2 bg-yellow-600 hover:bg-yellow-700 text-white">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Gestionar
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Esta SEMANA */}
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-t-lg">
            <CardTitle className="text-gray-800 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-yellow-600" />
              Esta SEMANA
            </CardTitle>
            <CardDescription className="text-gray-600">Casos para seguimiento próximo</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Exámenes de recuperación</p>
                  <p className="text-sm text-gray-600">8 estudiantes programados</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Programar
                  </Button>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Reuniones con padres</p>
                  <p className="text-sm text-gray-600">5 citas pendientes de agendar</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Agendar
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Este MES */}
        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
            <CardTitle className="text-gray-800 flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Este MES
            </CardTitle>
            <CardDescription className="text-gray-600">Objetivos y metas a mediano plazo</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Reducir deserción</p>
                  <p className="text-sm text-gray-600">Meta: &lt;5% • Actual: 6.2%</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Ver estrategia
                  </Button>
                </div>
              </div>
              <div className="flex items-start space-x-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Mejorar promedio general</p>
                  <p className="text-sm text-gray-600">Meta: 8.5 • Actual: 8.1</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    Plan académico
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}